import React, { useState } from 'react';
import axios from 'axios';
import { Heart, Copy, Check, Landmark, Utensils, ArrowRight, Shield, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DONATION_TIERS, CONTACT } from '../data/mock';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Donate = () => {
  const [amount, setAmount] = useState(350);
  const [custom, setCustom] = useState('');
  const [copied, setCopied] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const active = custom ? parseInt(custom, 10) || 0 : amount;
  const meals = Math.floor(active / 10);

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    toast({ title: 'Copied to clipboard', description: val });
    setTimeout(() => setCopied(null), 1500);
  };

  const donate = async () => {
    if (!active || active < 2) {
      toast({ title: 'Minimum donation is R2', description: 'Please choose or enter an amount.' });
      return;
    }
    if (!name.trim() || !email.trim()) {
      toast({ title: 'Please add your details', description: 'Name and email are required.' });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/donations/checkout`, {
        name,
        email,
        amount_zar: active,
        campaign: 'general',
        origin: window.location.origin,
      });
      if (res.data?.redirectUrl) {
        toast({
          title: `Redirecting to secure checkout...`,
          description: `Thank you for your R${active} donation!`,
        });
        window.location.href = res.data.redirectUrl;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Something went wrong';
      toast({ title: 'Payment could not start', description: String(msg).slice(0, 200) });
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute -left-32 top-20 w-96 h-96 bg-[#FFEFE2] rounded-full blur-3xl opacity-70" />
      <div className="absolute -right-32 bottom-10 w-96 h-96 bg-[#FFB347]/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block px-4 py-1.5 bg-[#F55B26]/10 text-[#F55B26] font-semibold text-xs uppercase tracking-widest rounded-full mb-4">
            Your Donation Counts
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0B3B44] leading-tight mb-4">
            Be the <span className="text-[#F55B26]">smile</span> today.
          </h2>
          <p className="text-lg text-slate-600">
            Every rand you donate becomes a meal, a moment of dignity, a step toward hope for someone who needs it most.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 bg-gradient-to-br from-[#FFF7F1] to-[#FFEFE2] rounded-3xl p-8 lg:p-10 border border-[#FFE4CF] shadow-lg">
            <h3 className="text-2xl font-extrabold text-[#0B3B44] mb-2">Choose your impact</h3>
            <p className="text-sm text-slate-600 mb-6">Approx. R10 provides one meal. Pick an amount below.</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {DONATION_TIERS.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => { setAmount(tier.amount); setCustom(''); }}
                  className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                    !custom && amount === tier.amount
                      ? 'border-[#F55B26] bg-white shadow-md'
                      : 'border-transparent bg-white/60 hover:bg-white hover:border-[#FFB347]'
                  }`}
                >
                  <div className="text-2xl font-extrabold text-[#0B3B44]">R{tier.amount}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-tight">{tier.label}</div>
                </button>
              ))}
            </div>

            <div className="mb-5">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2 block">Or enter a custom amount (ZAR)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">R</span>
                <Input
                  type="number"
                  min="2"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Enter amount"
                  className="pl-8 h-14 text-lg font-semibold bg-white border-2 border-[#FFE4CF] focus-visible:border-[#F55B26] focus-visible:ring-0 rounded-xl"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-5">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-12 bg-white border-2 border-[#FFE4CF] focus-visible:border-[#F55B26] focus-visible:ring-0 rounded-xl"
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (for receipt)"
                className="h-12 bg-white border-2 border-[#FFE4CF] focus-visible:border-[#F55B26] focus-visible:ring-0 rounded-xl"
              />
            </div>

            <div className="bg-white rounded-2xl p-5 mb-5 border border-[#FFE4CF]">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F55B26]/10 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-[#F55B26]" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Your R{active || 0} provides</div>
                  <div className="text-2xl font-extrabold text-[#0B3B44]">
                    ~{meals} meals
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={donate}
              disabled={!active || loading}
              className="w-full h-14 bg-[#F55B26] hover:bg-[#E14D1F] text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Preparing secure checkout...
                </>
              ) : (
                <>
                  <Heart className="w-5 h-5 mr-2" fill="white" />
                  Donate R{active || 0} to Hello Foundation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
              <Shield className="w-3.5 h-3.5" />
              Secure online donation to <span className="font-semibold text-[#0B3B44]">Hello Foundation</span>
            </div>
          </div>

          <div className="lg:col-span-2 bg-[#0B3B44] rounded-3xl p-8 text-white shadow-lg relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#F55B26]/20 rounded-full blur-2xl" />
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mb-4">
                <Landmark className="w-6 h-6 text-[#FFB347]" />
              </div>
              <h3 className="text-2xl font-extrabold mb-2">Direct EFT / Bank Transfer</h3>
              <p className="text-sm text-white/70 mb-6">Prefer traditional bank transfer? Use the details below.</p>

              <div className="space-y-3">
                {[
                  { k: 'Account Name', v: 'Hello Foundation', key: 'name' },
                  { k: 'Bank', v: CONTACT.bank.name, key: 'bank' },
                  { k: 'Account Number', v: CONTACT.bank.accNo, key: 'acc' },
                  { k: 'Branch Code', v: CONTACT.bank.branch, key: 'branch' },
                ].map((row) => (
                  <div key={row.key} className="flex items-center justify-between bg-white/5 backdrop-blur border border-white/10 rounded-xl p-3">
                    <div>
                      <div className="text-[10px] text-white/60 uppercase tracking-widest">{row.k}</div>
                      <div className="font-mono font-bold text-white">{row.v}</div>
                    </div>
                    <button
                      onClick={() => copy(row.v, row.key)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      aria-label={`Copy ${row.k}`}
                    >
                      {copied === row.key ? (
                        <Check className="w-4 h-4 text-[#FFB347]" />
                      ) : (
                        <Copy className="w-4 h-4 text-white/70" />
                      )}
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/60">
                Please use your name as payment reference. All donations are used directly for our feeding and mental wellness programs.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Donate;
